import { PageLoader, type Jobs } from '@seek/libs-shared';
import { useParams, useRouteLoaderData } from 'react-router';
import { CompanyProfilesPage } from '../../../paths';
import { useService } from '../../../shared/providers/serviceProvider';
import { NoIndexTag } from '../../components/NoIndexTag/NoIndexTag';
import { mapToSeoData } from '../../components/seoStructuredData/mapper';
import { SeoStructuredData } from '../../components/seoStructuredData/SeoStructuredData';
import { OPEN_JOBS_LISTING_SIZE_FOR_SALARIES } from './salariesLoader';
import { SalariesPage } from './SalariesPage';
import { SalariesPageByRole } from './SalariesPageByRole';
import type { SalariesTabModel } from './salariesTabModel';

interface SalariesPageContainerProps {
  onActiveTabChanged: (item: string | undefined) => void;
  trackJobsPaginationClicked: ({
    searchTotalCount,
    pageNumber,
  }: {
    pageNumber: number;
    searchTotalCount?: number;
  }) => void;
  trackRandomCompanyProfileLinkClicked: ({
    linkedCompanyId,
  }: {
    linkedCompanyId: string;
  }) => void;
  trackReviewsClicked: () => void;
}

export const SalariesPageContainer = ({
  trackJobsPaginationClicked,
  trackRandomCompanyProfileLinkClicked,
  onActiveTabChanged,
  trackReviewsClicked,
}: SalariesPageContainerProps) => {
  const { companyProfileService } = useService();
  const salariesHubPageModel = useRouteLoaderData<SalariesTabModel>(
    CompanyProfilesPage.Salaries,
  );
  const salariesRolePageModel = useRouteLoaderData<SalariesTabModel>(
    CompanyProfilesPage.SalariesRole,
  );

  const params = useParams();
  const isSalariesHubRoute = params.roleId === undefined;

  const getPaginatedJobs = async (
    organisationId: string,
    page?: number,
  ): Promise<Jobs> => {
    const res = await companyProfileService.getJobs(
      organisationId,
      page,
      OPEN_JOBS_LISTING_SIZE_FOR_SALARIES,
    );
    return res;
  };

  const salaryModel = isSalariesHubRoute
    ? salariesHubPageModel
    : salariesRolePageModel;

  const Page = isSalariesHubRoute ? SalariesPage : SalariesPageByRole;

  if (!salaryModel) {
    return <PageLoader />;
  }

  return (
    <>
      <SeoStructuredData data={mapToSeoData(salaryModel)} />
      {!salaryModel.shouldIndex && <NoIndexTag />}
      <Page
        model={salaryModel}
        getPaginatedJobs={getPaginatedJobs}
        trackJobsPaginationClicked={trackJobsPaginationClicked}
        trackRandomCompanyProfileLinkClicked={
          trackRandomCompanyProfileLinkClicked
        }
        onActiveTabChanged={onActiveTabChanged}
        trackReviewsClicked={trackReviewsClicked}
      />
    </>
  );
};
